<template>
    <TableAllocationInfoButton
        :showTransferOption="showTransferOption"
        :showShowApplicableDiscountsOption="true"
        @onRenameSelected="onRenameSelected"
        @onTransferSelectedItemsSelected="onTransferSelectedItemsSelected"
        @onCheckoutSelected="onCheckoutSelected"
        @onPrintSelected="onPrintSelected"
        @onAdjustEndUsersCountSelected="onAdjustEndUsersCountSelected"
        @onShowApplicableDiscountsSelected="onShowApplicableDiscountsSelected"
    />
    <RenameBillModal ref="renameBillModalComponent" />
    <ApplicableDiscountsModal ref="billApplicableDiscountsModalComponent" />
</template>

<script setup lang="ts">
    import { Popup } from "@/components/dialog/Popup";
    import { Factory } from "@/data/dao/Factory";
    import { BillDao } from "@/data/dao/service/BillDao";
    import { OperatorDao } from "@/data/dao/service/OperatorDao";
    import { dataEvents } from "@/data/events/dataEvents";
    import type { IBill } from "@/data/model/Bill";
    import { OperatorTransferClaimValue } from "@/data/model/Enums";
    import type { IBillEvent } from "@/data/model/Events";
    import type { IProfile } from "@/data/model/Profile";
    import { useBillStore } from "@/data/stores/bill";
    import { usePrintStore } from "@/data/stores/print";
    import { useTableAllocationStore } from "@/data/stores/tableAllocation";
    import { Format } from "@/data/util/Format";
    import { replaceString } from "@/data/util/String";
    import { OperatorClient } from "@/operator/OperatorClient";
    import { operatorEvents } from "@/operator/events/operatorEvents";
    import { trans } from "@/operator/localization/Trans";
    import { translations } from "@/shared/localization/translations";
    import { logError } from "@/shared/logging/errors";
    import { onMounted, onUnmounted, ref } from "vue";

    import TableAllocationInfoButton from "@/components/tableAllocation/TableAllocationInfoButton.vue";
    import ApplicableDiscountsModal from "@/operator/components/allocation/ApplicableDiscountsModal.vue";
    import RenameBillModal from "@/operator/components/allocation/RenameBillModal.vue";

    const allocation = useTableAllocationStore();
    const bill = useBillStore();
    const print = usePrintStore();

    const renameBillModalComponent = ref<InstanceType<typeof RenameBillModal> | null>(null);
    const billApplicableDiscountsModalComponent = ref<InstanceType<typeof ApplicableDiscountsModal> | null>(null);

    const showTransferOption = ref<boolean>(false);

    onMounted(async () => {
        const profile = OperatorClient.context.profile;
        showTransferOption.value = await hasAccessToTransferBill(profile, bill.currentBill);

        dataEvents.operatorSocket.BillOpened.add(onBillsChanged);
        dataEvents.operatorSocket.BillClosed.add(onBillsChanged);
    });

    onUnmounted(async () => {
        dataEvents.operatorSocket.BillOpened.remove(onBillsChanged);
        dataEvents.operatorSocket.BillClosed.remove(onBillsChanged);
    });

    async function onBillsChanged(ev: IBillEvent) {
        if (ev.tableId == allocation.tableId) {
            const profile = OperatorClient.context.profile;
            showTransferOption.value = await hasAccessToTransferBill(profile, bill.currentBill);
        }
    }

    async function hasAccessToTransferBill(profile: IProfile | undefined, currentBill: IBill | null): Promise<boolean> {
        if (profile?.operatorBillTransferGrant === OperatorTransferClaimValue.CanTransferOwn) {
            const billDao = Factory.getInstance<BillDao>(BillDao);
            if (currentBill != null) {
                const lastOperator = await billDao.getBillLastOperator({ billId: currentBill.id });
                return profile.identityId === lastOperator?.identityId;
            }

            return false;
        } else {
            return profile != null && profile.operatorBillTransferGrant !== OperatorTransferClaimValue.CannotTransfer;
        }
    }

    function onRenameSelected() {
        renameBillModalComponent.value?.show();
    }

    async function onPrintSelected() {
        try {
            await print.printInterimBill(bill.currentBill!.id, allocation!.locationId!, allocation.location!.language);
            Popup.show({
                title: trans.print.printReceipt,
                message: translations.print.orderReceiptPrinted,
            });
        } catch (ex) {
            logError(ex);
            Popup.show({
                title: translations.print.failedPrintReceipt,
                message: translations.print.failedPrintReceipt,
            });
        }
    }

    async function onTransferSelectedItemsSelected() {
        try {
            const billDao = Factory.getInstance<BillDao>(BillDao);
            await billDao.enableTransferBillItems({ billId: bill.currentBill!.id });

            operatorEvents.views.ShowRoomPlan.dispatch({
                showSettingsRoomPlan: false,
                locationId: allocation.locationId,
                roomId: allocation.roomId,
                transferBillOptions: {
                    currentTableId: allocation.tableId!,
                    billId: bill.currentBill!.id,
                    selectedItems: bill.selectedItemsList,
                },
            });
        } catch (ex) {
            logError(ex);
            Popup.show({
                title: translations.general.error,
                message: translations.general.operationFailed,
            });
        }
    }

    function onCheckoutSelected() {
        const table = allocation.fullTableInfo;

        if (table == null || table.allocations == 0) return;

        Popup.show({
            title: translations.changeAllocationDetailsModal.resetTable,
            message: replaceString(
                translations.changeAllocationDetailsModal.resetTableText,
                table.allocations + "",
                Format.htmlEscape(table.name)!
            ),
            cancelButton: translations.general.cancel,
            confirmButton: translations.changeAllocationDetailsModal.resetTable,
            confirmHandler: async () => {
                try {
                    await allocation.removeGuests();
                } catch (ex) {
                    logError(ex);
                    Popup.show({
                        title: translations.general.error,
                        message: translations.changeAllocationDetailsModal.endUsersCouldNotBeRemoved,
                    });
                }
            },
        });
    }

    async function onAdjustEndUsersCountSelected(count: number) {
        try {
            const operatorDao = Factory.getInstance<OperatorDao>(OperatorDao);
            await operatorDao.updateBillManualEndUsersCount(bill.currentBill!.id, {
                manualEndUsersCount: count,
            });
        } catch (ex) {
            logError(ex);
            Popup.show({
                title: translations.general.error,
                message: translations.tableAllocationContextMenu.adjustEndUsersCountFailed,
            });
        }
    }

    async function onShowApplicableDiscountsSelected() {
        billApplicableDiscountsModalComponent.value?.show();
    }
</script>
